/**
 * Shared math helpers. All allocation-free. Owned by core.
 *
 * NOTE (ARCHITECTURE.md §6): do NOT use Math.hypot in simulation hot paths — its precision
 * differs between engines and breaks bit-identical determinism. Use len2/dist2 here.
 */

export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;
export const RAD = 180 / Math.PI;

export const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
export const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
export const lerp = (a, b, t) => a + (b - a) * t;
export const invLerp = (a, b, v) => (b === a ? 0 : (v - a) / (b - a));
export const remap = (v, a, b, c, d) => c + (d - c) * clamp01(invLerp(a, b, v));
export const sign = (v) => (v > 0 ? 1 : v < 0 ? -1 : 0);

/** Smoothstep in [0,1]. */
export const smoothstep = (t) => { t = clamp01(t); return t * t * (3 - 2 * t); };
export const smootherstep = (t) => { t = clamp01(t); return t * t * t * (t * (t * 6 - 15) + 10); };

/** Frame-rate independent exponential approach. `rate` is the fraction closed per second. */
export const damp = (a, b, rate, dt) => lerp(a, b, 1 - Math.exp(-rate * dt));

/** Move toward at fixed speed. */
export function moveToward(cur, target, maxDelta) {
  const d = target - cur;
  if (Math.abs(d) <= maxDelta) return target;
  return cur + Math.sign(d) * maxDelta;
}

/** 2D length without Math.hypot. */
export const len2 = (x, y) => Math.sqrt(x * x + y * y);
export const len3 = (x, y, z) => Math.sqrt(x * x + y * y + z * z);
export const dist2 = (ax, ay, bx, by) => { const dx = bx - ax, dy = by - ay; return Math.sqrt(dx * dx + dy * dy); };
export const distSq2 = (ax, ay, bx, by) => { const dx = bx - ax, dy = by - ay; return dx * dx + dy * dy; };
export const distSq3 = (ax, ay, az, bx, by, bz) => {
  const dx = bx - ax, dy = by - ay, dz = bz - az; return dx * dx + dy * dy + dz * dz;
};

/** Shortest signed angular difference, result in (-PI, PI]. */
export function angleDelta(from, to) {
  let d = (to - from) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d <= -Math.PI) d += TAU;
  return d;
}

export function lerpAngle(a, b, t) { return a + angleDelta(a, b) * t; }
export function dampAngle(a, b, rate, dt) { return a + angleDelta(a, b) * (1 - Math.exp(-rate * dt)); }

/** Closest point on segment AB to P, in 2D (xz plane). Returns t in [0,1]. */
export function closestOnSegment2(px, pz, ax, az, bx, bz) {
  const abx = bx - ax, abz = bz - az;
  const denom = abx * abx + abz * abz;
  if (denom < 1e-9) return 0;
  let t = ((px - ax) * abx + (pz - az) * abz) / denom;
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

/** Signed perpendicular distance from P to the infinite line through A with direction D (unit, 2D). */
export function signedLineDist2(px, pz, ax, az, dx, dz) {
  return (px - ax) * dz - (pz - az) * dx;
}

/** Deterministic hash of two ints → [0,1). Useful for stable per-instance variation. */
export function hash2(x, y) {
  let h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x2545f491) >>> 0;
  return h / 4294967296;
}

/** Ease curves for juice. All take and return [0,1]. */
export const ease = {
  outQuad: (t) => 1 - (1 - t) * (1 - t),
  outCubic: (t) => 1 - Math.pow(1 - t, 3),
  outQuint: (t) => 1 - Math.pow(1 - t, 5),
  inQuad: (t) => t * t,
  inOutQuad: (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
  outBack: (t) => { const c1 = 1.70158, c3 = c1 + 1; return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2); },
  outElastic: (t) => {
    if (t === 0 || t === 1) return t;
    const c4 = TAU / 3;
    return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
  },
  outBounce: (t) => {
    const n1 = 7.5625, d1 = 2.75;
    if (t < 1 / d1) return n1 * t * t;
    if (t < 2 / d1) return n1 * (t -= 1.5 / d1) * t + 0.75;
    if (t < 2.5 / d1) return n1 * (t -= 2.25 / d1) * t + 0.9375;
    return n1 * (t -= 2.625 / d1) * t + 0.984375;
  },
  /** Overshoot pop used for combo/score text. */
  pop: (t) => (t < 0.35 ? ease.outBack(t / 0.35) : 1 + Math.sin((t - 0.35) / 0.65 * Math.PI) * 0.06),
};

/** Rolling percentile helper — used by the profiler and anything needing p95/p99. */
export function percentile(sortedAsc, p) {
  const n = sortedAsc.length;
  if (n === 0) return 0;
  if (n === 1) return sortedAsc[0];
  const idx = (n - 1) * p;
  const lo = Math.floor(idx), hi = Math.ceil(idx);
  if (lo === hi) return sortedAsc[lo];
  return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * (idx - lo);
}
